/**
 * Pure geometry shared by the API (which stores seat positions) and the editor
 * (which previews them). One implementation, so what the operator sees on
 * screen is exactly what gets written.
 *
 * No I/O, no DOM, no Konva.
 */

export type Point = [number, number];

const DEG = Math.PI / 180;

export function rotatePoint([x, y]: Point, degrees: number, [cx, cy]: Point = [0, 0]): Point {
  const a = degrees * DEG;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  const dx = x - cx;
  const dy = y - cy;
  return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
}

/** Two decimals is plenty for a canvas and keeps stored numbers readable. */
export const round2 = (value: number): number => Math.round(value * 100) / 100;

// --- Rows -------------------------------------------------------------------

export interface RowPattern {
  seat_count: number;
  /** Position of the first seat before rotation and curve. */
  x: number;
  y: number;
  /** Distance between neighboring seat centers, measured along the row. */
  seat_spacing: number;
  /** Degrees, clockwise (canvas y points down). 0 runs left to right. */
  rotation: number;
  /**
   * 0 for a straight row. Otherwise the radius of the arc the seats sit on:
   * positive bows the ends *down* the canvas (away from a stage drawn above),
   * negative bows them up. The middle of the row stays at (x + width/2, y).
   */
  curve_radius: number;
}

export interface PlacedSeat {
  x: number;
  y: number;
  /** Degrees; the direction the seat faces, so curved rows turn with the arc. */
  rotation: number;
}

/**
 * Where each seat of a row goes. Seats are spaced `seat_spacing` apart along
 * the row (along the arc, for a curved one), then the whole row is rotated
 * about its first seat and translated to (x, y).
 */
export function placeRowSeats(pattern: RowPattern): PlacedSeat[] {
  const n = Math.max(0, Math.floor(pattern.seat_count));
  if (n === 0) return [];
  const spacing = pattern.seat_spacing;
  const width = (n - 1) * spacing;
  const radius = pattern.curve_radius;

  const local: PlacedSeat[] = [];
  if (!radius || Math.abs(radius) < 1e-6) {
    for (let i = 0; i < n; i += 1) local.push({ x: i * spacing, y: 0, rotation: 0 });
  } else {
    const r = Math.abs(radius);
    const sign = radius > 0 ? 1 : -1;
    const step = spacing / r; // radians between neighbors, measured at the center
    for (let i = 0; i < n; i += 1) {
      const phi = (i - (n - 1) / 2) * step;
      // Arc center sits `r` below (sign > 0) or above the row's midpoint, so the
      // midpoint itself stays on the straight-row line.
      const x = width / 2 + r * Math.sin(phi);
      const y = sign * (r - r * Math.cos(phi));
      local.push({ x, y, rotation: (sign * phi) / DEG });
    }
  }

  return local.map((seat) => {
    const [rx, ry] = rotatePoint([seat.x, seat.y], pattern.rotation);
    return { x: round2(pattern.x + rx), y: round2(pattern.y + ry), rotation: round2(pattern.rotation + seat.rotation) };
  });
}

export type Numbering = 'SEQUENTIAL' | 'ODD' | 'EVEN';

/**
 * Seat numbers for a row. ODD/EVEN step by two (house-left / house-right
 * numbering); `reverse` numbers right-to-left without touching physical order.
 */
export function seatNumbers(count: number, first: number, numbering: Numbering, reverse: boolean): string[] {
  const step = numbering === 'SEQUENTIAL' ? 1 : 2;
  const numbers = Array.from({ length: Math.max(0, count) }, (_, index) => String(first + index * step));
  return reverse ? numbers.reverse() : numbers;
}

// --- Tables -----------------------------------------------------------------

export interface TableShape {
  shape: 'ROUND' | 'RECT';
  x: number;
  y: number;
  width: number;
  height: number;
  rotation: number;
}

/**
 * Seat centers around a table, evenly spaced, starting at the "top" (12
 * o'clock) and running clockwise, `gap` outside the edge. Round tables place
 * seats on an ellipse; rectangular ones walk the perimeter so a long table gets
 * more seats along its long sides.
 */
export function placeTableSeats(table: TableShape, count: number, gap = 14): PlacedSeat[] {
  const n = Math.max(0, Math.floor(count));
  if (n === 0) return [];
  const local: PlacedSeat[] = [];

  if (table.shape === 'ROUND') {
    const rx = table.width / 2 + gap;
    const ry = table.height / 2 + gap;
    for (let i = 0; i < n; i += 1) {
      const angle = -Math.PI / 2 + (i / n) * 2 * Math.PI;
      const x = rx * Math.cos(angle);
      const y = ry * Math.sin(angle);
      local.push({ x, y, rotation: angle / DEG + 90 });
    }
  } else {
    const w = table.width + 2 * gap;
    const h = table.height + 2 * gap;
    const perimeter = 2 * (w + h);
    for (let i = 0; i < n; i += 1) {
      // Start a half-step in so a single seat sits mid-edge rather than on a corner.
      let d = ((i + 0.5) / n) * perimeter;
      let x: number;
      let y: number;
      let rotation: number;
      if (d < w) {
        x = -w / 2 + d;
        y = -h / 2;
        rotation = 0;
      } else if ((d -= w) < h) {
        x = w / 2;
        y = -h / 2 + d;
        rotation = 90;
      } else if ((d -= h) < w) {
        x = w / 2 - d;
        y = h / 2;
        rotation = 180;
      } else {
        d -= w;
        x = -w / 2;
        y = h / 2 - d;
        rotation = 270;
      }
      local.push({ x, y, rotation });
    }
  }

  return local.map((seat) => {
    const [rx, ry] = rotatePoint([seat.x, seat.y], table.rotation);
    return { x: round2(table.x + rx), y: round2(table.y + ry), rotation: round2(table.rotation + seat.rotation) };
  });
}

// --- Polygons ---------------------------------------------------------------

/** Andrew's monotone chain. Returns the hull counter-clockwise (in y-down canvas terms, clockwise on screen). */
export function convexHull(points: Point[]): Point[] {
  const unique = Array.from(new Map(points.map((p) => [`${p[0]},${p[1]}`, p])).values()).sort(
    (a, b) => a[0] - b[0] || a[1] - b[1],
  );
  if (unique.length < 3) return unique;
  const cross = (o: Point, a: Point, b: Point) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: Point[] = [];
  for (const p of unique) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0) lower.pop();
    lower.push(p);
  }
  const upper: Point[] = [];
  for (let i = unique.length - 1; i >= 0; i -= 1) {
    const p = unique[i]!;
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0) upper.pop();
    upper.push(p);
  }
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

export function centroid(points: Point[]): Point {
  if (points.length === 0) return [0, 0];
  const sum = points.reduce<Point>((acc, [x, y]) => [acc[0] + x, acc[1] + y], [0, 0]);
  return [sum[0] / points.length, sum[1] / points.length];
}

/** Pushes every vertex of a polygon `distance` further from its centroid. */
export function expandPolygon(points: Point[], distance: number): Point[] {
  const [cx, cy] = centroid(points);
  return points.map(([x, y]) => {
    const dx = x - cx;
    const dy = y - cy;
    const length = Math.hypot(dx, dy) || 1;
    return [round2(x + (dx / length) * distance), round2(y + (dy / length) * distance)];
  });
}

/** Ray casting; points on an edge count as inside often enough for selection. */
export function pointInPolygon([x, y]: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const [xi, yi] = polygon[i]!;
    const [xj, yj] = polygon[j]!;
    const intersects = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export function boundsOf(points: Point[]): Bounds | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [x, y] of points) {
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

export function pointInBounds([x, y]: Point, bounds: Bounds): boolean {
  return x >= bounds.minX && x <= bounds.maxX && y >= bounds.minY && y <= bounds.maxY;
}

// --- Existing seats: what the editor and the API infer from them ------------

export interface PlacedSeatLike {
  x: number;
  y: number;
  sort_order: number;
  row_id?: string | null;
  table_id?: string | null;
  rotation?: number;
}

/** Half the typical gap between neighboring seats, capped so sparse rows do not get giant chairs. */
export function estimateSeatRadius(seats: PlacedSeatLike[]): number {
  const byGroup = new Map<string, PlacedSeatLike[]>();
  for (const seat of seats) {
    const key = seat.row_id ?? seat.table_id ?? '';
    const list = byGroup.get(key) ?? [];
    list.push(seat);
    byGroup.set(key, list);
  }
  const gaps: number[] = [];
  for (const list of byGroup.values()) {
    const sorted = [...list].sort((a, b) => a.sort_order - b.sort_order);
    for (let i = 1; i < sorted.length && gaps.length < 400; i += 1) {
      gaps.push(Math.hypot(sorted[i]!.x - sorted[i - 1]!.x, sorted[i]!.y - sorted[i - 1]!.y));
    }
  }
  if (gaps.length === 0) return 8;
  gaps.sort((a, b) => a - b);
  const median = gaps[Math.floor(gaps.length / 2)]!;
  return Math.max(3, Math.min(20, median * 0.42));
}

export interface RowAxis {
  /** Unit vector along the row, from first seat to last. */
  direction: Point;
  /** Typical distance between neighbors. */
  spacing: number;
  /** Degrees, the direction as a rotation (0 = +x). */
  rotation: number;
}

/**
 * Which way a row runs and how far apart its seats are, from the seats it
 * already has. A single seat has no direction of its own, so its rotation
 * decides; `fallbackSpacing` is used when there is nothing to measure.
 */
export function rowAxis(seats: PlacedSeatLike[], fallbackSpacing = 24): RowAxis {
  const sorted = [...seats].sort((a, b) => a.sort_order - b.sort_order);
  if (sorted.length >= 2) {
    const first = sorted[0]!;
    const last = sorted[sorted.length - 1]!;
    const dx = last.x - first.x;
    const dy = last.y - first.y;
    const length = Math.hypot(dx, dy);
    if (length > 1e-6) {
      const gaps: number[] = [];
      for (let i = 1; i < sorted.length; i += 1) {
        gaps.push(Math.hypot(sorted[i]!.x - sorted[i - 1]!.x, sorted[i]!.y - sorted[i - 1]!.y));
      }
      gaps.sort((a, b) => a - b);
      const spacing = gaps[Math.floor(gaps.length / 2)] || fallbackSpacing;
      return { direction: [dx / length, dy / length], spacing, rotation: round2(Math.atan2(dy, dx) / DEG) };
    }
  }
  const rotation = sorted[0]?.rotation ?? 0;
  const a = rotation * DEG;
  return { direction: [Math.cos(a), Math.sin(a)], spacing: fallbackSpacing, rotation };
}

/** How far along a row's axis a point sits, relative to the first seat. */
export function projectOnRow(point: Point, seats: PlacedSeatLike[], axis: RowAxis): number {
  const sorted = [...seats].sort((a, b) => a.sort_order - b.sort_order);
  const origin = sorted[0] ?? { x: 0, y: 0 };
  return (point[0] - origin.x) * axis.direction[0] + (point[1] - origin.y) * axis.direction[1];
}

/**
 * Where `count` more seats go past one end of a row, continuing its spacing
 * and direction (the last two seats decide, so a curved row keeps curving,
 * roughly).
 */
export function continueRow(seats: PlacedSeatLike[], count: number, end: 'END' | 'START', fallbackSpacing = 24): PlacedSeat[] {
  const sorted = [...seats].sort((a, b) => a.sort_order - b.sort_order);
  if (sorted.length === 0 || count <= 0) return [];
  const axis = rowAxis(sorted, fallbackSpacing);
  const edge = end === 'END' ? sorted[sorted.length - 1]! : sorted[0]!;
  const neighbor = end === 'END' ? sorted[sorted.length - 2] : sorted[1];
  let step: Point;
  if (neighbor) {
    const dx = edge.x - neighbor.x;
    const dy = edge.y - neighbor.y;
    const length = Math.hypot(dx, dy) || axis.spacing;
    step = [(dx / length) * axis.spacing, (dy / length) * axis.spacing];
  } else {
    const sign = end === 'END' ? 1 : -1;
    step = [axis.direction[0] * axis.spacing * sign, axis.direction[1] * axis.spacing * sign];
  }
  const rotation = edge.rotation ?? axis.rotation;
  return Array.from({ length: count }, (_, i) => ({
    x: round2(edge.x + step[0] * (i + 1)),
    y: round2(edge.y + step[1] * (i + 1)),
    rotation,
  }));
}

/**
 * Seat numbers that continue a row's own scheme past one end: 1,2,3 → 4;
 * 2,4,6 → 8; 10,9,8 → 7 at the END and 11 at the START. Returns null when the
 * scheme cannot be read (letters, a single seat with nothing to compare, a
 * result below 1, or a collision), in which case the caller has to ask.
 */
export function continueNumbering(existing: string[], count: number, end: 'END' | 'START'): string[] | null {
  const numbers = existing.map((n) => (/^\d+$/.test(n) ? Number(n) : NaN));
  if (numbers.length === 0 || numbers.some((n) => Number.isNaN(n))) return null;
  let step: number;
  let edge: number;
  if (numbers.length === 1) {
    step = end === 'END' ? 1 : -1;
    edge = numbers[0]!;
  } else if (end === 'END') {
    edge = numbers[numbers.length - 1]!;
    step = edge - numbers[numbers.length - 2]!;
  } else {
    edge = numbers[0]!;
    step = edge - numbers[1]!;
  }
  if (step === 0) return null;
  const taken = new Set(existing);
  const out: string[] = [];
  for (let i = 1; i <= count; i += 1) {
    const n = edge + step * i;
    if (n < 1 || !Number.isInteger(n) || taken.has(String(n))) return null;
    out.push(String(n));
  }
  return out;
}

/**
 * A padded outline around a section's seats. A convex hull when the seats
 * enclose an area; a box around the line when they are collinear (one straight
 * row is a section too); null only when there are no seats.
 */
export function sectionHull(seats: PlacedSeatLike[], padding: number): Point[] | null {
  if (seats.length === 0) return null;
  const hull = convexHull(seats.map((s) => [s.x, s.y] as Point));
  if (hull.length >= 3) return expandPolygon(hull, padding);
  const a = hull[0]!;
  const b = hull[hull.length - 1]!;
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const length = Math.hypot(dx, dy);
  const ux = length ? dx / length : 1;
  const uy = length ? dy / length : 0;
  const px = -uy;
  const py = ux;
  const corner = (p: Point, along: number, across: number): Point => [
    round2(p[0] + ux * along * padding + px * across * padding),
    round2(p[1] + uy * along * padding + py * across * padding),
  ];
  return [corner(a, -1, 1), corner(b, 1, 1), corner(b, 1, -1), corner(a, -1, -1)];
}

/*
 * Reading an outline column, in one place (D-184).
 *
 * `parsePolygon` existed twice — once inside `SeatMapView` and once inside the
 * admin map panel — which is the shape of problem D-139 was about. Now that an
 * outline can hold several polygons, two readers would have meant the editor
 * and the storefront disagreeing about how many shapes a GA area has.
 *
 * Malformed geometry returns nothing rather than throwing: a bad drawing is a
 * drawing problem, and it must not take the map down with it.
 */

/** Every shape in an outline column: none, one, or several. */
export function shapesOf(json: string | null | undefined): Point[][] {
  if (!json) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return [];
  }
  const usable = (points: unknown): points is Point[] =>
    Array.isArray(points) && points.length >= 3 && points.every((p) => Array.isArray(p) && p.length === 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  const value = parsed as { kind?: unknown; points?: unknown; polygons?: unknown };
  if (value.kind === 'polygon') return usable(value.points) ? [value.points] : [];
  if (value.kind === 'multipolygon') return Array.isArray(value.polygons) ? value.polygons.filter(usable) : [];
  return [];
}

/**
 * What to store for a set of shapes.
 *
 * One shape stays a plain `polygon`, so nothing already drawn is rewritten into
 * a new shape of data just because the reader learned to handle more.
 */
export function shapesToGeometryJson(shapes: readonly Point[][], source: 'drawn' | 'seats' = 'drawn'): string | null {
  const kept = shapes.filter((points) => points.length >= 3);
  if (kept.length === 0) return null;
  if (kept.length === 1) return JSON.stringify({ kind: 'polygon', points: kept[0], source });
  return JSON.stringify({ kind: 'multipolygon', polygons: kept, source: 'drawn' });
}

/**
 * Where a shape's name goes (D-208).
 *
 * The centroid is the obvious answer and the wrong one: a GA area drawn around
 * a floor of tables has its middle exactly where the tables are, so the name
 * landed on top of them and on whatever they were labeled.
 *
 * A name belongs at the **top of the shape**, the way a section heading does.
 * This walks down from the top edge until it finds a point genuinely inside the
 * polygon — concave shapes have a top-center that is outside them, and an
 * arched or notched room is exactly the case where the naive answer looks
 * broken — and falls back to the centroid if nothing inside is found, because
 * a label in a slightly odd place beats no label at all.
 */
export function labelAnchor(polygon: Point[], inset = 0): Point {
  const bounds = boundsOf(polygon);
  if (!bounds || polygon.length < 3) return centroid(polygon);

  const [cx] = centroid(polygon);
  const height = bounds.maxY - bounds.minY;
  /*
   * Walked from the top down, stopping at the first point genuinely inside.
   * An ordinary room answers on the first or second step, so the name sits at
   * the top where it belongs; a horseshoe or an arch, whose top center is in
   * the gap, keeps going until it reaches the solid part.
   */
  const steps = 20;
  for (let step = 0; step <= steps; step += 1) {
    const y = bounds.minY + inset + (height * step) / steps;
    if (y > bounds.maxY) break;
    if (pointInPolygon([cx, y], polygon)) return [cx, y];
  }
  return centroid(polygon);
}

/**
 * How to set a name inside a shape: its center, the direction of its longest
 * side, how long that side is and how thick the shape is across it.
 *
 * A bleacher block is a long, thin strip set at an angle; a name printed flat
 * at a fixed width spilled out of both ends. Following the strip and sizing to
 * its thickness keeps the name inside. `angle` is in degrees, turned so text
 * never reads upside down.
 */
export interface LabelFrame {
  center: Point;
  angle: number;
  length: number;
  thickness: number;
}

export function labelFrame(polygon: Point[]): LabelFrame | null {
  if (polygon.length < 3) return null;
  const center = centroid(polygon);
  // The direction the shape spreads along (its principal axis), so a curved
  // block made of many short edges still reads as one long strip.
  let sxx = 0;
  let syy = 0;
  let sxy = 0;
  for (const [x, y] of polygon) {
    const dx = x - center[0];
    const dy = y - center[1];
    sxx += dx * dx;
    syy += dy * dy;
    sxy += dx * dy;
  }
  const radians = 0.5 * Math.atan2(2 * sxy, sxx - syy);
  const [ux, uy] = [Math.cos(radians), Math.sin(radians)];
  let min = Infinity;
  let max = -Infinity;
  for (const [x, y] of polygon) {
    const along = (x - center[0]) * ux + (y - center[1]) * uy;
    min = Math.min(min, along);
    max = Math.max(max, along);
  }
  const length = max - min;
  if (!(length > 0)) return null;
  let twiceArea = 0;
  for (let index = 0; index < polygon.length; index += 1) {
    const [ax, ay] = polygon[index]!;
    const [bx, by] = polygon[(index + 1) % polygon.length]!;
    twiceArea += ax * by - bx * ay;
  }
  let angle = (radians * 180) / Math.PI;
  if (angle > 90) angle -= 180;
  if (angle <= -90) angle += 180;
  return { center, angle, length, thickness: Math.abs(twiceArea) / 2 / length };
}

/** Everything a manifest draws, for working out what part of the canvas is worth showing. */
export interface DrawnLayout {
  /*
   * `sort_order`, `row_id` and `table_id` are optional but worth supplying:
   * the seat radius is estimated from the spacing *within* a row or table, and
   * without the grouping every seat in the room is treated as one row, which
   * makes the estimate coarser than it needs to be.
   */
  seats: { x: number; y: number; sort_order?: number; row_id?: string | null; table_id?: string | null }[];
  tables: { x: number; y: number; width: number; height: number }[];
  sections: { geometry_json?: string | null }[];
  ga_areas: { geometry_json?: string | null }[];
}

/**
 * The box that actually has something in it (D-267).
 *
 * A canvas is whatever the imported artboard happened to be, and an artboard is
 * frequently mostly empty — a floor exported from a drawing of the whole
 * building sits in the lower third of a tall white rectangle. This is the box
 * the seats, tables, sections and lawns really occupy, which is what somebody
 * means when they say "crop it in".
 *
 * A seat is a point but draws as a disc, and a table is drawn from its center,
 * so both are grown by their own size before measuring. `padding` is then the
 * breathing room around the lot: a map cropped exactly to its seats has the
 * outermost ones touching the frame, which reads as a mistake.
 *
 * Returns null when there is nothing drawn at all, because the honest answer
 * for an empty room is "no opinion", not a zero-sized window.
 */
export function contentBounds(layout: DrawnLayout, options: { seatRadius?: number; padding?: number } = {}): Bounds | null {
  const radius = options.seatRadius ?? estimateSeatRadius(layout.seats.map((s) => ({ ...s, sort_order: s.sort_order ?? 0 })));
  const points: Point[] = [];
  for (const seat of layout.seats) {
    points.push([seat.x - radius, seat.y - radius], [seat.x + radius, seat.y + radius]);
  }
  for (const table of layout.tables) {
    // Tables are drawn from the center, and their seats ring the outside, so
    // half the larger dimension is the honest reach.
    const reach = Math.max(table.width, table.height) / 2;
    points.push([table.x - reach, table.y - reach], [table.x + reach, table.y + reach]);
  }
  for (const section of layout.sections) for (const shape of shapesOf(section.geometry_json)) points.push(...shape);
  for (const area of layout.ga_areas) for (const shape of shapesOf(area.geometry_json)) points.push(...shape);

  const bounds = boundsOf(points);
  if (!bounds) return null;
  const padding = options.padding ?? Math.max(24, radius * 2);
  return {
    minX: bounds.minX - padding,
    minY: bounds.minY - padding,
    maxX: bounds.maxX + padding,
    maxY: bounds.maxY + padding,
  };
}

/**
 * What one map unit is, in inches (item 104, D-299).
 *
 * A manifest's coordinates are arbitrary: an SVG import brings whatever the
 * drawing used, `generate-rows` brings whatever spacing somebody typed. Two
 * seats and a tape measure turn every other dimension in the room into a real
 * one — which is what lets a plan print at a stated scale and lets anybody ask
 * whether an aisle is wide enough.
 *
 * Returns null rather than throwing on a measurement that says nothing: two
 * seats in the same place have no distance to scale by, and neither does a
 * claim of zero inches.
 */
export function unitsPerInch(a: Point, b: Point, inches: number): number | null {
  if (!(inches > 0)) return null;
  const distance = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (!(distance > 0)) return null;
  return distance / inches;
}

/** Inches between two points in a room whose scale is known. */
export function inchesBetween(a: Point, b: Point, unitsPerInchValue: number): number | null {
  if (!(unitsPerInchValue > 0)) return null;
  return Math.hypot(b[0] - a[0], b[1] - a[1]) / unitsPerInchValue;
}

/**
 * A round number of feet that fits inside `maxUnits`, for a scale bar.
 *
 * Picks from a human ladder rather than dividing: a bar labeled "10 ft" is
 * read at a glance and one labeled "8.3 ft" is not.
 */
export function scaleBarFeet(maxUnits: number, unitsPerInchValue: number): { feet: number; units: number } | null {
  if (!(unitsPerInchValue > 0) || !(maxUnits > 0)) return null;
  const perFoot = unitsPerInchValue * 12;
  for (const feet of [100, 50, 25, 20, 10, 5, 2, 1]) {
    if (feet * perFoot <= maxUnits) return { feet, units: feet * perFoot };
  }
  return null;
}
